import { useState } from 'react';
import { Bed, Bath, Car, Maximize, MapPin, Heart } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatCurrency, formatArea, propertyTypeLabels, transactionTypeLabels, getPropertyImage, getWhatsAppLink } from '../lib/utils';

export const PropertyCard = ({ property, onClick }) => {
  const mainImage = getPropertyImage(property, 0);
  const price = property.transaction_type === 'rent' ? property.pricing?.rent_price : property.pricing?.sale_price;
  const priceLabel = property.transaction_type === 'rent' ? '/mês' : '';
  
  return (
    <div
      data-testid={`property-card-${property.id}`}
      className="property-card group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl border border-slate-100 cursor-pointer"
      onClick={() => onClick?.(property)}
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={mainImage}
          alt={property.title}
          className="property-image w-full h-full object-cover"
          loading="lazy"
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {property.featured && (
            <Badge className="bg-brand-gold text-brand-blue font-semibold">Destaque</Badge>
          )}
          {property.tags?.slice(0, 2).map((tag) => (
            <Badge key={tag} variant="secondary" className="bg-white/90 text-slate-700">{tag}</Badge>
          ))}
        </div>
        {/* Transaction Type */}
        <div className="absolute top-3 right-3">
          <Badge className="bg-brand-blue text-white">
            {transactionTypeLabels[property.transaction_type]}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Price */}
        <div className="mb-2">
          <span className="price-tag text-2xl font-bold text-brand-blue">
            {formatCurrency(price)}
          </span>
          <span className="text-slate-500 text-sm">{priceLabel}</span>
        </div>

        {/* Title */}
        <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2 line-clamp-1">
          {property.title}
        </h3>

        {/* Location */}
        <div className="flex items-center text-slate-500 text-sm mb-4">
          <MapPin className="w-4 h-4 mr-1" />
          <span>{property.address?.neighborhood || 'Toledo'}, {property.address?.city || 'PR'}</span>
        </div>

        {/* Features */}
        <div className="flex items-center gap-4 text-slate-600 text-sm">
          {property.rooms?.bedrooms > 0 && (
            <div className="flex items-center gap-1">
              <Bed className="w-4 h-4" />
              <span>{property.rooms.bedrooms}</span>
            </div>
          )}
          {property.rooms?.bathrooms > 0 && (
            <div className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              <span>{property.rooms.bathrooms}</span>
            </div>
          )}
          {property.garage?.parking_spots > 0 && (
            <div className="flex items-center gap-1">
              <Car className="w-4 h-4" />
              <span>{property.garage.parking_spots}</span>
            </div>
          )}
          {property.sizes?.built_area_m2 && (
            <div className="flex items-center gap-1">
              <Maximize className="w-4 h-4" />
              <span>{property.sizes.built_area_m2}m²</span>
            </div>
          )}
        </div>

        {/* Property Type Badge */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <Badge variant="outline" className="text-xs">
            {propertyTypeLabels[property.property_type]}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export const PropertyModal = ({ property, open, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!property) return null;

  const images = property.images?.length > 0 
    ? property.images 
    : [getPropertyImage(property)];
  
  const price = property.transaction_type === 'rent' ? property.pricing?.rent_price : property.pricing?.sale_price;
  const priceLabel = property.transaction_type === 'rent' ? '/mês' : '';

  const whatsappMessage = `Olá! Tenho interesse no imóvel: ${property.title}`;
  const whatsappLink = getWhatsAppLink('5545999092653', whatsappMessage);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="property-modal">
        {/* Image Gallery */}
        <div className="relative h-72 md:h-96 bg-slate-100">
          <img
            src={images[currentImageIndex]?.startsWith('http') ? images[currentImageIndex] : `${process.env.REACT_APP_BACKEND_URL}${images[currentImageIndex]}`}
            alt={property.title}
            className="w-full h-full object-cover"
          />
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                />
              ))}
            </div>
          )}
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <Badge className="bg-brand-blue text-white">
              {transactionTypeLabels[property.transaction_type]}
            </Badge>
            <Badge variant="outline" className="bg-white/90">
              {propertyTypeLabels[property.property_type]}
            </Badge>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                {property.title}
              </h2>
              <div className="flex items-center text-slate-500">
                <MapPin className="w-4 h-4 mr-1" />
                <span>
                  {property.address?.hide_exact_address 
                    ? `${property.address?.neighborhood}, ${property.address?.city}` 
                    : `${property.address?.street || ''} ${property.address?.number || ''}, ${property.address?.neighborhood || ''}, ${property.address?.city || 'Toledo'}`
                  }
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="price-tag text-3xl font-bold text-brand-blue">
                {formatCurrency(price)}
              </div>
              <span className="text-slate-500">{priceLabel}</span>
              {property.pricing?.condo_fee && (
                <div className="text-sm text-slate-500 mt-1">
                  Condomínio: {formatCurrency(property.pricing.condo_fee)}/mês
                </div>
              )}
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
            {property.rooms?.bedrooms > 0 && (
              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5 text-brand-blue" />
                <div>
                  <div className="font-semibold">{property.rooms.bedrooms}</div>
                  <div className="text-xs text-slate-500">Quartos</div>
                </div>
              </div>
            )}
            {property.rooms?.bathrooms > 0 && (
              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-brand-blue" />
                <div>
                  <div className="font-semibold">{property.rooms.bathrooms}</div>
                  <div className="text-xs text-slate-500">Banheiros</div>
                </div>
              </div>
            )}
            {property.garage?.parking_spots > 0 && (
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-brand-blue" />
                <div>
                  <div className="font-semibold">{property.garage.parking_spots}</div>
                  <div className="text-xs text-slate-500">Vagas</div>
                </div>
              </div>
            )}
            {property.sizes?.built_area_m2 && (
              <div className="flex items-center gap-2">
                <Maximize className="w-5 h-5 text-brand-blue" />
                <div>
                  <div className="font-semibold">{formatArea(property.sizes.built_area_m2)}</div>
                  <div className="text-xs text-slate-500">Área Construída</div>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="font-heading font-semibold text-lg mb-2">Descrição</h3>
            <p className="text-slate-600 leading-relaxed">{property.description || 'Sem descrição disponível.'}</p>
          </div>

          {/* Amenities */}
          {property.amenities?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-lg mb-3">Comodidades</h3>
              <div className="flex flex-wrap gap-2">
                {property.amenities.map((amenity) => (
                  <Badge key={amenity} variant="outline" className="py-1.5 px-3">
                    {amenity}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              data-testid="whatsapp-modal-btn"
              asChild
              className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white rounded-full"
            >
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                Falar no WhatsApp
              </a>
            </Button>
            <Button
              data-testid="call-modal-btn"
              asChild
              variant="outline"
              className="flex-1 h-12 rounded-full border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
            >
              <a href="tel:+554599909-2653">
                Ligar Agora
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
